import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Empty,
  Popover,
  Typography,
  Row,
  Col,
  Modal,
} from "antd";
import { useParams, useNavigate } from "react-router-dom";
import {
  WarningOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  EyeOutlined,
  LeftOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { translate, get_language } from "../../locale";
import DashServices from "../../services/DashServices";
import moment from "moment-timezone";

const { Title, Text } = Typography;

const PatientSelfEvaluationAnswersTable = (reload) => {
  const [loading, setLoading] = useState(false);
  const { username } = useParams();
  const [data, setData] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (reload !== null) {
      loadData();
    }
  }, [reload]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await DashServices.getSelfEvaluationsAnswer(username);
      if (res.status >= 200 && res.status < 300) {
        if (
          res.data.survey_answer !== undefined &&
          res.data.survey_answer.length > 0
        ) {
          setData(res.data.survey_answer);
        }
        if (res.data.detail !== "") {
          const errors = {
            "You do not have permission to perform this action.": translate(
              "No tienes permisos para realizar esta acción"
            ),
          };
          setErrorMsg(errors[res.data.detail]);
        }
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };
  const navigate = useNavigate();

  const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 24,
      }}
      spin
    />
  );

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-2">
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            className="border-0"
            onClick={() => setSelectedSurvey(record)}
          />
        </div>
      ),
    },
    {
      title: "Fecha y hora",
      dataIndex: "register_date",
      key: "register_date",
      render: (_, record) =>
        moment(record.register_date)
          .tz(moment.tz.guess())
          .format(
            get_language === "en" ? "MM/DD/YYYY HH:mm" : "DD/MM/YYYY HH:mm"
          ),
      sorter: (a, b) => {
        if (a.register_date < b.register_date) return -1;
        if (a.register_date > b.register_date) return 1;
        return 0;
      },
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      render: (_, record) => record.survey_user.survey.title,
      sorter: (a, b) => {
        if (a.survey_user.survey.title < b.survey_user.survey.title) return -1;
        if (a.survey_user.survey.title > b.survey_user.survey.title) return 1;
        return 0;
      },
    },
    {
      title: "Resultado",
      dataIndex: "result",
      key: "result",
      render: (_, record) => {
        let text = "-";
        if (record.result_text) {
          text = record.result_text;
        } else if (record.result_num) {
          text = record.result_num;
        }

        return text;
      },
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (_, record) => {
        if (record.survey_user.answer.status === "complete") {
          return translate("Completo");
        } else if (record.survey_user.answer.status === "incomplete") {
          return translate("Incompleto");
        }
      },
      sorter: (a, b) => {
        if (a.survey_user.answer.status < b.survey_user.answer.status)
          return -1;
        if (a.survey_user.answer.status > b.survey_user.answer.status) return 1;
        return 0;
      },
    },
    {
      title: translate("Remitente"),
      dataIndex: "sender",
      key: "sender",
      render: (_, record) =>
        record.sender ? record.sender.fullname : "Protocolo automático",
    },
  ];

  return (
    <Row className="w-full">
      <Col span={24}>
        <Table
          rowKey={(i) => i.id}
          className="w-full"
          dataSource={data}
          columns={columns}
          loading={
            loading
              ? { indicator: antIcon, className: "text-custom_blue" }
              : false
          }
          locale={{
            emptyText:
              errorMsg === "" ? (
                <Empty description={translate("No hay datos")} />
              ) : (
                <Empty
                  image={<CloseCircleOutlined className="text-7xl mt-4" />}
                  description={translate(errorMsg)}
                />
              ),
            sortTitle: translate("Ordenar"),
            triggerDesc: translate("Ordenar (descendente)"),
            triggerAsc: translate("Ordenar (ascendente)"),
            cancelSort: translate("Cancelar orden"),
            filterTitle: translate("Filtros"),
            filterConfirm: translate("Aceptar"),
            filterReset: translate("Limpiar"),
            filterEmptyText: translate("No hay filtros"),
            filterCheckall: translate("Seleccinar todos"),
            filterSearchPlaceholder: translate("Buscar en filtros"),
          }}
        />
      </Col>

      <Modal
        open={selectedSurvey !== null}
        title={translate("Respuestas")}
        onOk={null}
        onCancel={() => setSelectedSurvey(null)}
        footer={false}
        width="40%"
      >
        {selectedSurvey !== null && <SurveyRender data={selectedSurvey} />}
      </Modal>
    </Row>
  );
};

const SurveyRender = ({ data }) => {
  const [answer, setAnswer] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    console.log("data", data);
    if (data !== null) {
      getAnswers();
    }
  }, [data]);

  const getAnswers = async (id) => {
    setLoading(true);
    try {
      const response = await DashServices.getSelfEvaluationsAnswer(
        data.survey_user.user.username,
        data.id
      );
      if (response.status == 200) {
        setAnswer(response.data.survey_answer[0].answer);
      }
      setLoading(false);
    } catch (error) {
      console.log("getAnswers error", error);
      setLoading(false);
    }
  };

  const statuses = {
    complete: translate("Completo"),
    incomplete: translate("Incompleto"),
    pending: translate("Pendiente"),
  };

  const options = {
    danger: { text: "Riesgo alto", color: "#E90303" },
    warning: { text: "Riesgo medio", color: "#E90303" },
    normal: { text: "Riesgo bajo", color: "#3BBE50" },
  };

  const getRisk = (data) => {
    if (data.response) {
      console.log(data.response);
      return options[data.response].text;
    }
    return "";
  };

  const getRiskStyle = (data) => {
    if (data.response && data.response == "normal") {
      return {
        flex: 1,
        marginLeft: 16,
        marginRight: 16,
        marginTop: 4,
        color: "#3BBE50",
      };
    }
    return {
      flex: 1,
      marginLeft: 16,
      marginRight: 16,
      marginTop: 4,
      color: "#E90303",
    };
  };

  const answerContainer = (item) => {
    if (item.name != "result") {
      return (
        <Row className="flex-col border-0 border-b border-solid border-custom_gray p-4">
          <Col>
            <Title level={5} style={{ marginBottom: 8 }}>
              {item.title === "" ? item.name : item.title}:
            </Title>
            {(typeof item.response === "string" ||
              typeof item.response === "number") && (
              <Text style={{ marginLeft: 12 }}>{item.response}</Text>
            )}
            {typeof item.response === "object" &&
              Array.isArray(item.response) &&
              item.response.map((i) => (
                <Text style={{ marginLeft: 12 }}>{i}</Text>
              ))}

            {typeof item.response === "object" &&
              !Array.isArray(item.response) &&
              Object.entries(item.response).map(([key, value]) => {
                if (key != "value") {
                  return (
                    <Text style={{ marginLeft: 12 }}>
                      {key === "text"
                        ? typeof value === "string"
                          ? `${value}`
                          : value.es
                        : `${key}: ${value}`}{" "}
                    </Text>
                  );
                }
              })}

            {typeof item.response === "boolean" && (
              <Text style={{ marginLeft: 12 }}>
                {item.response ? "Si" : "No"}
              </Text>
            )}
          </Col>
        </Row>
      );
    } else {
      return (
        <Row className="flex-col p-4">
          <Col>
            <Title level={5} style={{ marginBottom: 8 }}>
              {item.title === "" ? item.name : item.title}
            </Title>
            <Text style={getRiskStyle(item)}>{getRisk(item)}</Text>
          </Col>
        </Row>
      );
    }
  };

  return (
    <Row>
      <Col span={24} className="my-6">
        <Title level={3} className="text-center">
          {translate("Autoevaluación")}
        </Title>
      </Col>
      <Col span={24} className="mb-6">
        <Title
          level={4}
          className="text-center"
          style={{ marginBottom: 0, color: "rgb(132, 132, 132)" }}
        >
          {data.survey_user.survey.title}
        </Title>
        <Title
          level={5}
          className="text-center"
          style={{ marginTop: 0, marginBottom: 0, color: "rgb(132, 132, 132)" }}
        >
          {moment(data.register_date)
            .tz(moment.tz.guess())
            .format(
              get_language === "en" ? "MM/DD/YYYY HH:mm" : "DD/MM/YYYY HH:mm:ss"
            )}
        </Title>
        <Title
          level={5}
          className="text-center"
          style={{ marginTop: 0, marginBottom: 0, color: "rgb(132, 132, 132)" }}
        >
          {statuses[data.survey_user.answer.status]}
        </Title>
      </Col>

      {loading && (
        <Col className="flex justify-center items-center w-full h-80">
          <LoadingOutlined />
        </Col>
      )}
      {!loading &&
        answer.map((item, key) => (
          <Col key={key} span={24}>
            {answerContainer(item)}
          </Col>
        ))}
    </Row>
  );
};

export default PatientSelfEvaluationAnswersTable;
